import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { decompressURLSearchParams } from '@modules/common/utils';
import { useGetFeedDuration, useGetFeedWhereClause } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { FeedActionType } from '@modules/feed/store/searchActionType';
import { getSearchMetadataUrl } from '@modules/feed/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { getNewAnalysisCompatibleWithOld } from 'v2/core/repository/edge-server/utils/newAnalysisCompatibleWithOld';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useGetUser } from 'v2/core/services/user';

interface UpdateFeedAnalysisActionProps {
  shouldSaveDashboardFilters?: boolean;
}

export const useUpdateFeedAnalysisAction = ({
  shouldSaveDashboardFilters,
}: UpdateFeedAnalysisActionProps = {}) => {
  const {
    tenant: { id: tenantID, slug: tenantSlug },
  } = useGetUser();

  const navigate = useNavigate();
  const location = useLocation();

  const searchAnalysis = useAnalysisStore((state) => state);
  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const prompt = useFeedStore((state) => state.prompt);

  const { updateAnalysis, isLoading: isUpdatingAnalysis } = useUpdateAnalysis({
    ...getNewAnalysisCompatibleWithOld(searchAnalysis),
    metadataV2: undefined,
    editToken: searchAnalysis?.editToken ?? '',
    createdBy: searchAnalysis?.createdBy,
  });

  const whereClause = useGetFeedWhereClause({
    shouldIncludeDashboardFilters: shouldSaveDashboardFilters,
  });
  const duration = useGetFeedDuration();

  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause,
    dateRange: duration as EQLProps['dateRange'],
  };

  const { eql: analysisEql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const isLoading = isUpdatingAnalysis;

  useFullscreenLoader({ isLoading });

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const onUpdateSearchDashboard = async () => {
    try {
      if (!searchAnalysis) {
        return;
      }

      const metadata = getSearchMetadataUrl({
        tenantSlug,
        whereClause,
        duration,
        isSearchToFeedEnabled,
      });

      if (searchAnalysis) {
        await updateAnalysis({
          variables: {
            isReportingDisabled: searchAnalysis.isReportingDisabled,
            id: searchAnalysis.id,
            tenantID,
            metadata,
            scope: searchAnalysis.scope,
            editToken: searchAnalysis.editToken ?? '',
            createdBy: searchAnalysis.createdBy.id,
            context: {
              eqlExpression: analysisEql ?? '',
              prompt,
            },
          },
        });
      }

      searchAnalysis.onUpdate(metadata);

      const searchParams = new URLSearchParams(
        decompressURLSearchParams(window.location.search),
      );

      const areDashboardFiltersPresentInURL =
        searchParams.has('dashboardDuration') ||
        searchParams.has('dashboardWhereClause');

      if (!areDashboardFiltersPresentInURL) {
        navigate(
          {
            search: '',
          },
          {
            state: location.state,
            replace: true,
          },
        );
      }

      dispatch({
        type: FeedActionType.SET_UPDATE_QUERY,
        payload: {
          whereClause,
          duration,
        },
      });
    } catch {
      // do nothing, already handled by useUpdateFeedAnalysis
    }
  };

  return { onUpdateSearchDashboard, isLoading };
};
